/**
 * @fileoverview A public plant's or bed's own page under a gardener's garden page:
 * grow.gardengameplan.com/@amy/cherokee-purple, /@amy/bed-a. No account
 * needed.
 *
 * Both pages wear the garden page's look (gardenPageSections, itemPageSections):
 * a Share button in the corner of a full-width photo scroller (each public
 * photo with the day it was taken over it; Grow's mark is the public top bar's
 * only), opened on the photo the garden page's card was showing (?photo=N),
 * then the name in the page's serif, a line under it and the stage as a chip,
 * the gardener's words in a card, and at the foot the green "Grow your own
 * like Amy" card with "Start your garden free", crediting her garden.
 *
 * A plant's page: its one name as the garden page's card says it ("Cherokee
 * Purple Tomato", the backend's names: services/grow/plantNaming), "Bed A ·
 * grown by Amy" under it (the name linking back to the garden page), its
 * stage with the day it began ("Growing since Oct 9"), the share picture it went
 * out with (when it went out as a composed picture), the gardener's words from
 * its newest share with their day, its stages with their dates when there are
 * two or more ("Mar 1 Seeding · May 2 Transplanting"), the Plant Doctor
 * checkups the gardener saved to its notes (CheckupsSection, at #checkups,
 * where a garden-page card's "1 checkup" link opens it; each can turn the photo
 * scroller to its photo), and "More from this garden" as three small cards with
 * "See the whole garden".
 *
 * A bed's page: its own picture and the latest photo of each public plant in it
 * as the scroller (or the green bar), its name and description with a link
 * back, the words of its newest share, and its public plants as the garden
 * page's cards.
 *
 * React Router 6 matches whole segments only, so App gives this page
 * "/:handle/:item", every two-segment path no named page claims; it takes
 * only a first segment starting with "@". Anything else, a garden no one
 * has, or a plant or bed that is not public (or not there) goes where an
 * unknown path goes, as the garden page does: Today, or sign-in first. An
 * address typed in capitals or with accents is put right in place.
 *
 * What is public is the backend's to say (services/gardens/publicGarden);
 * opening the page counts one visit per browser (gardens getGardenItem).
 *
 * Where: the route /:handle/:item (App.tsx), public, outside the Layout.
 * API: GET /gardens/by-slug/:slug/:item (getGardenItem; ?count=0 once this
 * browser has counted a visit).
 * Settings: none; what is public is the gardener's Public switches.
 * Words (en/es): gardenPage.page.* (grownBy, title, timeline, stageSince,
 * moreHeading, moreSeeAll, backTo, bedPlants, bedEmpty,
 * latestFrom, itemShareText, cardPhotos, unavailable, stage.<type>),
 * sitewide.loading; the invitation's gardenPage.page.invite*.
 * Watch out: a missing or private item sends the visitor to /today (sign-in
 * first when signed out), not to a "not found" page. One that could not be
 * read on our side (a 500, no connection; D31, lib/serverFailure) says the
 * standard sentence instead, reported to Sentry, with the support address
 * as a link; a 410 stays gardenPage.page.unavailable.
 *
 * @module GardenItemPage
 */

import React from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, Sprout } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import {
  gardenItemPathFor,
  gardenPathFor,
  gardenUrlFor,
  getGardenItem,
  type GardenItem,
  type GardenPhoto,
} from '@/shared/api/gardens';
import Spinner from '@/shared/components/ui/Spinner';
import { GardenFrame, GardenInvite, LargePicture } from './gardenPageParts';
import { PHOTO_PARAM, PlantGrid, SECTION_HEADING_CLASS, WordsCard } from './gardenPageSections';
import { ItemColumn, ItemHeading, ItemHero } from './itemPageSections';
import { CheckupsSection } from './CheckupsSection';
import { apiSrc, DatePartsContext, datePartsOf, photosOfPlant, useGardenCredit, useShortDay } from './lib/gardenPage';
import { isUnavailable } from '@/shared/lib/errorMessage';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

/** The page at /@:slug/:item (App's /:handle/:item). */
export const GardenItemPage: React.FC = () => {
  const { handle = '', item: rawItem = '' } = useParams<{ handle: string; item: string }>();
  const { reactTranslate: t } = useTranslation();
  const navigate = useNavigate();
  /** The address's ?query (?photo=2) and #fragment (#checkups), if any. */
  const { search, hash } = useLocation();
  /** Not "@" in front: not a garden's page at all. */
  const notAHandle = !handle.startsWith('@');
  /** The addresses as typed in the path: the backend folds them, lower-cases them and answers 404 for any that could not be one. */
  const slug = notAHandle ? '' : handle.slice(1);
  const item = rawItem;
  const notAnAddress = notAHandle;
  /** Which address the read below is for: what it read only counts for that address. */
  const key = `${slug}/${item}`;
  /**
   * The read, kept with the address it was for. Moving to another plant or bed
   * (a "More from this garden" card) changes the address before the new read
   * is back; the old page must not count for the new address, or the address
   * check below would send the visitor back to the old one.
   */
  const [read, setRead] = React.useState<{
    key: string;
    page: GardenItem | null;
    missing: boolean;
    /** The gardener is suspended: the page says only that it is unavailable. */
    unavailable: boolean;
    /** What the read threw when it failed on our side (a 500, no connection); null otherwise. */
    ours: unknown;
  } | null>(null);
  const current = read && read.key === key ? read : null;
  const page = current?.page ?? null;
  const missing = current?.missing ?? false;
  const unavailable = current?.unavailable ?? false;
  const ours = current?.ours ?? null;

  React.useEffect(() => {
    if (notAnAddress) return undefined;
    let cancelled = false;
    const readFor = `${slug}/${item}`;
    getGardenItem(slug, item)
      .then((data) => { if (!cancelled) setRead({ key: readFor, page: data, missing: false, unavailable: false, ours: null }); })
      .catch((error) => {
        if (cancelled) return;
        setRead({ key: readFor, page: null, missing: true, unavailable: isUnavailable(error), ours: isServerFailure(error) ? error : null });
      });
    return () => { cancelled = true; };
  }, [slug, item, notAnAddress]);

  // Typed in capitals or with accents, or the garden has moved: put right in place.
  React.useEffect(() => {
    if (!page) return;
    const own = page.kind === 'plant' ? page.plant.slug : page.bed.slug;
    if (!page.slug || !own) return;
    // The address's ?photo= and #fragment (#checkups) go along with the corrected address.
    if (handle !== `@${page.slug}` || rawItem !== own) navigate(`${gardenItemPathFor(page.slug, own)}${search}${hash}`, { replace: true });
  }, [page, handle, rawItem, search, hash, navigate]);

  useGardenCredit(page?.code, page?.gardener?.name);

  // A suspended gardener's page: unavailable, and nothing more.
  if (unavailable) {
    return (
      <GardenFrame>
        <p className="text-muted-foreground">{t('gardenPage.page.unavailable')}</p>
      </GardenFrame>
    );
  }

  // Not read, on our side: the standard sentence, not the way out an unknown path takes.
  if (ours) {
    return (
      <GardenFrame>
        <ServerFailureNote error={ours} where="Garden item page" className="text-base text-muted-foreground" />
      </GardenFrame>
    );
  }

  // Not a garden's page, or nothing public at it: where any unknown path goes.
  if (notAnAddress || missing) return <Navigate to="/today" replace />;

  if (!page) {
    return (
      <GardenFrame>
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      </GardenFrame>
    );
  }

  const gardenPath = gardenPathFor(page.code, page.slug);
  const name = page.gardener?.name ?? null;

  return (
    <GardenFrame bare>
      {/* Dates as the gardener shows them (Profile > Public pages: the day, the year). */}
      <DatePartsContext.Provider value={datePartsOf(page.dates)}>
        {/* Keyed by the address, so a new plant or bed starts on its own photo, not the last one's. */}
        {page.kind === 'plant'
          ? <PlantBody key={key} page={page} gardenPath={gardenPath} name={name} />
          : <BedBody key={key} page={page} gardenPath={gardenPath} name={name} />}
      </DatePartsContext.Provider>
    </GardenFrame>
  );
};

/** What both bodies are told. */
interface BodyProps<K extends GardenItem['kind']> {
  /** The plant's or the bed's page, as the backend sent it. */
  page: Extract<GardenItem, { kind: K }>;
  /** The garden page's path, for the links back. */
  gardenPath: string;
  /** The gardener's name as shown, or none. */
  name: string | null;
}

/**
 * The line under a card of words: its day, and who shared it.
 *
 * @param day - YYYY-MM-DD, or nothing.
 * @param name - The gardener's name as shown, or nothing.
 * @param t - The translator.
 * @param shortDay - Says a day as the gardener shows dates (useShortDay).
 * @returns The line, or null with neither.
 */
function wordsLine(
  day: string | null | undefined,
  name: string | null,
  t: (key: string, params?: Record<string, string | number>) => string,
  shortDay: (day: string | null | undefined) => string | null,
): string | null {
  return [shortDay(day), name ? t('gardenPage.page.latestFrom', { name }) : null].filter(Boolean).join(' · ') || null;
}

/**
 * A plant's or bed's own page address on the share host
 * (share.gardengameplan.com/@amy/cherokee-purple), so its Share gives the
 * plant's page and the plant's preview, not the garden's.
 *
 * @param code - The garden's code.
 * @param slug - The garden's address, without its "@".
 * @returns The page's full address.
 */
function useItemUrl(code: string, slug: string | null | undefined): string {
  const { item = '' } = useParams<{ item: string }>();
  return `${gardenUrlFor(code, slug)}/${item}`;
}

/**
 * The photo an item page opens on: ?photo=N (from 1) when a garden-page card
 * was showing that photo, else the first. Out of range reads as the first.
 *
 * @param search - The address's query.
 * @param count - How many photos the page has.
 * @returns The photo's place, from 0.
 */
function photoAsked(search: URLSearchParams, count: number): number {
  const asked = Number(search.get(PHOTO_PARAM)) - 1;
  return Number.isInteger(asked) && asked > 0 && asked < count ? asked : 0;
}

/**
 * A plant's page: the photo scroller (opened on the photo the visitor was
 * looking at), its one name as the garden page's card says it (the backend's
 * names, services/grow/plantNaming: "Cherokee Purple Tomato"), the bed and
 * who grew it, its stage with the day it began ("Growing since Oct 9"), the
 * share picture it went out with, the gardener's words, its stages when
 * there are two or more, its Plant Doctor checkups, a few more plants from
 * the garden as small cards, then the invitation.
 *
 * @param props - BodyProps for a plant.
 */
const PlantBody: React.FC<BodyProps<'plant'>> = ({ page, gardenPath, name }) => {
  /** This plant's own address on the share host, what its Share gives (owner, 2026-10-15: a plant's share showed the garden's collage). */
  const itemUrl = useItemUrl(page.code, page.slug);
  const { reactTranslate: t } = useTranslation();
  const shortDay = useShortDay();
  const [search] = useSearchParams();
  const plant = page.plant;
  /** The plant's one name; its kind is not said beside it. */
  const title = plant.names.full;
  const more = plant.more;
  /** Its public photos, else the one picture the page used to lead with. */
  const own = photosOfPlant(plant);
  const photos: GardenPhoto[] = own.length
    ? own
    : plant.picture?.kind === 'photo' ? [{ src: plant.picture.url, date: plant.picture.packet ? null : plant.date ?? null, ...(plant.picture.packet ? { packet: true as const } : {}) }] : [];
  /** The photo the scroller shows: the one asked for, then whichever a checkup turns it to. */
  const [shown, setShown] = React.useState(() => photoAsked(search, photos.length));
  /** The stage now with the day it began, when the server says its newest recorded stage is that stage (stageSince). */
  const stageNow = plant.stage ? plant.stage.name : null;
  const since = stageNow && plant.stageSince ? shortDay(plant.stageSince) : null;
  // A point it ended at (Last Harvest, Expired) is said with its day, never "since" it.
  const stageText = stageNow && since ? t(plant.stage?.ended ? 'gardenPage.page.stageOn' : 'gardenPage.page.stageSince', { stage: stageNow, day: since }) : stageNow;

  /**
   * Turns the photo scroller to a checkup's photo and scrolls up to it.
   *
   * @param date - The photo's day (YYYY-MM-DD).
   */
  const showPhoto = (date: string): void => {
    const at = photos.findIndex((photo) => photo.date === date);
    if (at < 0) return;
    setShown(at);
    const calm = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: calm ? 'auto' : 'smooth' });
  };

  return (
    <>
      <ItemHero
        photos={photos}
        label={t('gardenPage.page.cardPhotos', { name: title })}
        link={itemUrl}
        title={title}
        show={shown}
        heading={(
          <ItemHeading
            title={title}
            stageText={stageText}
            line={(
              <>
                {[plant.bed].filter(Boolean).map((part) => <span key={part}>{part} · </span>)}
                {name ? (
                  <>
                    {t('gardenPage.page.grownBy')}{' '}
                    <Link to={gardenPath} className="font-medium text-foreground underline decoration-1 underline-offset-4 hover:decoration-2">{name}</Link>
                  </>
                ) : (
                  <Link to={gardenPath} className="underline decoration-1 underline-offset-4">{t('gardenPage.page.title')}</Link>
                )}
              </>
            )}
          />
        )}
      />
      <ItemColumn>

        {/* A share that went out as a composed picture keeps showing it, said to be the picture shared. */}
        {plant.picture?.kind === 'frame' && (
          <section>
            <h2 className={SECTION_HEADING_CLASS}>{t('gardenPage.page.sharedPicture')}</h2>
            <div className="mt-3"><LargePicture src={plant.picture.url} alt={plant.name} kind="frame" size={plant.picture.size} /></div>
          </section>
        )}

        {plant.words && <WordsCard words={plant.words} meta={wordsLine(plant.wordsDate, name, t, shortDay)} />}

        {/* One stage is the chip under the name already; a list from two up. */}
        {plant.timeline.length > 1 && (
          <section>
            <h2 className={SECTION_HEADING_CLASS}>{t('gardenPage.page.timeline')}</h2>
            <ol className="mt-3 space-y-1 rounded-2xl border bg-card p-4 text-sm">
              {plant.timeline.map((entry, index) => (
                <li key={`${entry.on}-${index}`} className="flex gap-3">
                  <span className="w-16 shrink-0 text-muted-foreground">{shortDay(entry.on)}</span>
                  <span>{entry.stage.name}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* The checkups the gardener saved to this plant's notes and did not hide. */}
        {plant.checkups && plant.checkups.length > 0 && (
          <CheckupsSection checkups={plant.checkups} by={name} onShowPhoto={showPhoto} />
        )}

        {more.plants.length > 0 && (
          <section>
            <h2 className={SECTION_HEADING_CLASS}>{t('gardenPage.page.moreHeading')}</h2>
            {/* The garden page's tile size: four across from a tablet, five on a wide screen (owner, 2026-10-14: the cards were too large). */}
            <ul className="mt-3 grid grid-cols-3 gap-3 md:grid-cols-4 xl:grid-cols-5">
              {more.plants.map((other, index) => {
                // Each by its one name (the backend's names), as the garden page's cards say it.
                const label = other.names.full;
                const to = other.slug && page.slug ? gardenItemPathFor(page.slug, other.slug) : gardenPath;
                return (
                  <li key={`${other.slug ?? other.variety}-${index}`} className="min-w-0">
                    <Link to={to} className="block overflow-hidden rounded-xl border bg-card hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      {other.photo
                        ? (
                          <span className="relative block aspect-square w-full bg-muted data-[small=true]:bg-transparent">
                            <FramedPhoto src={apiSrc(other.photo)} alt="" loading="lazy" draggable={false} />
                          </span>
                        )
                        : (
                          <span className="flex aspect-square w-full items-center justify-center bg-muted" aria-hidden="true">
                            <Sprout className="h-8 w-8 text-primary/35" />
                          </span>
                        )}
                      <span className="block px-2 py-1.5 font-grow text-sm font-bold leading-snug [overflow-wrap:anywhere]">{label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Link to={gardenPath} className="mt-2 inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline">
              {t('gardenPage.page.moreSeeAll')}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </section>
        )}

        <div className="mx-auto max-w-xl pt-4">
          <GardenInvite code={page.code} name={name} card />
        </div>
      </ItemColumn>
    </>
  );
};

/**
 * A bed's page: the photo scroller, name and description, words, its public
 * plants as cards, then the invitation.
 *
 * @param props - BodyProps for a bed.
 */
const BedBody: React.FC<BodyProps<'bed'>> = ({ page, gardenPath, name }) => {
  /** This bed's own address on the share host, what its Share gives. */
  const itemUrl = useItemUrl(page.code, page.slug);
  const { reactTranslate: t } = useTranslation();
  const shortDay = useShortDay();
  const [search] = useSearchParams();
  const bed = page.bed;
  const photos: GardenPhoto[] = bed.photos?.length ? bed.photos : bed.cover ? [{ src: bed.cover, date: null }] : [];
  /** The photo the scroller opens on: the one the garden page's card was showing (its collage counts as one, with three photos or more). */
  const [shown] = React.useState(() => photoAsked(search, photos.length + (photos.length >= 3 ? 1 : 0)));
  return (
    <>
      <ItemHero
        photos={photos}
        label={t('gardenPage.page.cardPhotos', { name: bed.name })}
        link={itemUrl}
        title={bed.name}
        show={shown}
        collage
        heading={(
          <ItemHeading
            title={bed.name}
            line={(
              <Link to={gardenPath} className="underline decoration-1 underline-offset-4 hover:decoration-2">
                {name ? t('gardenPage.page.backTo', { name }) : t('gardenPage.page.title')}
              </Link>
            )}
          />
        )}
      />
      <ItemColumn>
        {bed.description && <p className="text-base">{bed.description}</p>}

        {bed.words && <WordsCard words={bed.words} meta={wordsLine(bed.wordsDate, name, t, shortDay)} />}

        {bed.plants.length > 0
          ? <PlantGrid plants={bed.plants} gardenSlug={page.slug} heading={t('gardenPage.page.bedPlants')} />
          : (
            <section>
              <h2 className={SECTION_HEADING_CLASS}>{t('gardenPage.page.bedPlants')}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t('gardenPage.page.bedEmpty')}</p>
            </section>
          )}

        <div className="mx-auto max-w-xl pt-4">
          <GardenInvite code={page.code} name={name} card />
        </div>
      </ItemColumn>
    </>
  );
};

export default GardenItemPage;
